/**
 * BROWSER-BASED VIDEO RENDERING
 * Renders video with text overlays using Canvas + MediaRecorder
 * Supports Unicode/Indic languages with Google Fonts
 */

import { loadGoogleFonts, getFontForText, FONT_FAMILY } from './fonts';
import { convertToMP4 } from './videoConverter';

/**
 * Get optimal MIME type for MediaRecorder based on browser support
 * Prioritizes H.264/MP4 for maximum compatibility (iPhone, WhatsApp, etc.)
 * @returns {string} Supported MIME type
 */
const getOptimalMimeType = () => {
  // Priority order: MP4/H.264 first for iPhone/WhatsApp compatibility
  const types = [
    'video/mp4;codecs=h264,aac',           // MP4 with H.264 video + AAC audio (best compatibility)
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2', // H.264 baseline + AAC (iPhone compatible)
    'video/mp4',                           // Generic MP4 (Safari)
    'video/webm;codecs=h264,opus',         // WebM with H.264 (if supported)
    'video/webm;codecs=vp9,opus',          // WebM VP9 (Chrome fallback)
    'video/webm;codecs=vp8,opus',          // WebM VP8 (Firefox fallback)
    'video/webm'                           // Generic WebM (last resort)
  ];

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      console.log('📹 Using codec:', type);
      console.log('📱 iPhone/WhatsApp compatible:', type.includes('mp4') || type.includes('h264'));
      return type;
    }
  }

  // Last resort - warn user
  console.warn('⚠️ No MP4 codec available - output may not play on iPhone/WhatsApp');
  return 'video/webm';
};

/**
 * Wait for video to seek to a specific time
 * @param {HTMLVideoElement} video - Video element
 * @param {number} targetTime - Target time in seconds
 * @returns {Promise<void>}
 */
const waitForSeek = (video, targetTime) => {
  return new Promise((resolve) => {
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked);
      resolve();
    };
    video.addEventListener('seeked', onSeeked);
    video.currentTime = targetTime;
  });
};

/**
 * Draw a text layer on canvas with proper Unicode support
 * Matches the DOM preview positioning exactly
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} layer - Text layer object
 * @param {number} canvasWidth - Canvas width
 * @param {number} canvasHeight - Canvas height
 * @param {number} fontScale - Scale factor for fontSize (videoHeight / previewHeight)
 */
const drawTextLayer = (ctx, layer, canvasWidth, canvasHeight, fontScale = 1) => {
  // Get appropriate font for this text
  const fontFamily = getFontForText(layer.text);

  // Build font string
  const style = layer.italic ? 'italic' : 'normal';
  const weight = layer.bold ? 'bold' : 'normal';

  // Scale fontSize to match preview appearance on actual video dimensions
  // If preview is 400px tall and video is 1920px tall, fontScale = 1920/400 = 4.8
  // So a 32px font in preview becomes 153.6px in the video
  const fontSize = Math.round(layer.fontSize * fontScale);

  if (fontScale !== 1) {
    console.log(`📏 Font scaling: ${layer.fontSize}px (preview) → ${fontSize}px (video) [scale: ${fontScale.toFixed(2)}x]`);
  }

  ctx.font = `${style} ${weight} ${fontSize}px ${fontFamily}, ${FONT_FAMILY}`;
  ctx.fillStyle = layer.color;

  // Match DOM preview positioning:
  // DOM uses: left: x%, top: y%, transform: translate(-50%, -50%)
  // This means the CENTER of the text is at (x%, y%)
  // Canvas equivalent: textAlign center, textBaseline middle
  ctx.textAlign = 'center';  // Always center for matching transform: translate(-50%, -50%)
  ctx.textBaseline = 'middle';

  // Calculate position (percentage to pixels)
  // This matches the DOM's left/top percentage
  const x = (layer.x / 100) * canvasWidth;
  const y = (layer.y / 100) * canvasHeight;

  // Save context to apply text alignment separately if needed
  ctx.save();

  // Handle multi-line text (split by \n)
  const lines = layer.text.split('\n');
  const lineHeight = fontSize * 1.2; // 1.2x font size for line spacing (matches typical CSS line-height)

  if (lines.length > 1) {
    console.log(`📝 Multi-line text: ${lines.length} lines`, lines);
  }

  // Calculate total text block height for proper centering
  const totalTextHeight = lines.length * lineHeight;
  const startY = y - (totalTextHeight / 2) + (lineHeight / 2);

  // Set text alignment based on layer.align
  if (layer.align === 'left') {
    ctx.textAlign = 'left';
  } else if (layer.align === 'right') {
    ctx.textAlign = 'right';
  } else {
    ctx.textAlign = 'center'; // default
  }

  // For left/right alignment with centered positioning, we need to offset
  let xOffset = x;
  if (layer.align === 'left' || layer.align === 'right') {
    // Measure the widest line to calculate offset
    const maxWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
    if (layer.align === 'left') {
      xOffset = x - maxWidth / 2;
    } else if (layer.align === 'right') {
      xOffset = x + maxWidth / 2;
    }
  }

  // Draw each line
  lines.forEach((line, index) => {
    const lineY = startY + (index * lineHeight);
    ctx.fillText(line, xOffset, lineY);
  });

  ctx.restore();
};

/**
 * Main video rendering function
 * Composites video frames with text overlays using Canvas + MediaRecorder
 *
 * IMPORTANT: Text positioning matches the DOM preview exactly:
 * - Preview (TextOverlay.jsx): left: x%, top: y%, transform: translate(-50%, -50%)
 * - Canvas: textAlign: center, textBaseline: middle, position at (x%, y%)
 * - This ensures WYSIWYG - what you see in preview is what you get in download
 *
 * @param {string} videoUrl - Source video URL
 * @param {Array} textLayers - Array of text layer objects
 * @param {Object} options - Rendering options
 * @returns {Promise<Blob>} Video blob for download
 */
export const renderVideoWithText = async (videoUrl, textLayers, options = {}) => {
  const {
    width,
    height,
    previewWidth = 600,
    previewHeight = 400,
    frameRate = 30,
    quality = 'high',
    onProgress = null,
    convertToMP4Format = true // Always convert to MP4 for iPhone/WhatsApp compatibility
  } = options;

  console.log('🎬 Starting video rendering...', {
    videoUrl,
    textLayerCount: textLayers.length,
    frameRate,
    quality
  });

  // Declare resources outside try so finally can clean them up
  let video = null;
  let audioContext = null;
  let combinedStream = null;

  try {
    // Step 1: Load Google Fonts
    console.log('📥 Loading Google Fonts...');
    await loadGoogleFonts();

    // Step 2: Setup video element (hidden from user)
    console.log('📹 Loading video...');
    video = document.createElement('video');
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    video.muted = false; // Keep unmuted so audio is decoded (we'll capture it silently via Web Audio API)
    video.volume = 1; // Full volume for capture
    video.playsInline = true; // Prevent fullscreen on mobile
    video.setAttribute('webkit-playsinline', 'true'); // Older iOS
    video.style.position = 'fixed';
    video.style.top = '-9999px';
    video.style.left = '-9999px';
    video.style.width = '10px';
    video.style.height = '10px';
    video.style.opacity = '0.01';
    video.style.pointerEvents = 'none';
    video.style.zIndex = '-1';
    document.body.appendChild(video); // Need to append for captureStream to work

    // Wait for video metadata
    await new Promise((resolve, reject) => {
      video.onloadedmetadata = resolve;
      video.onerror = () => reject(new Error('Failed to load video'));
      // Timeout after 30 seconds
      setTimeout(() => reject(new Error('Video load timeout')), 30000);
    });

    // Check if source video has audio
    const hasAudioTrack = video.mozHasAudio !== undefined
      ? video.mozHasAudio
      : (video.webkitAudioDecodedByteCount !== undefined
        ? video.webkitAudioDecodedByteCount > 0
        : true); // Assume true if we can't detect

    console.log('🎵 Source video audio check:', hasAudioTrack ? 'Has audio' : 'No audio detected');

    const duration = video.duration;
    const videoWidth = width || video.videoWidth;
    const videoHeight = height || video.videoHeight;

    // Calculate font scaling factor to match preview dimensions
    // This ensures text appears the same size relative to video as it does in preview
    const fontScale = videoHeight / previewHeight;

    console.log('✅ Video loaded:', {
      duration: `${duration.toFixed(2)}s`,
      dimensions: `${videoWidth}x${videoHeight}`,
      previewDimensions: `${previewWidth}x${previewHeight}`,
      fontScale: fontScale.toFixed(2),
      fps: frameRate
    });

    // Step 3: Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = videoWidth;
    canvas.height = videoHeight;
    const ctx = canvas.getContext('2d', {
      alpha: false, // No transparency needed - improves performance
      desynchronized: true // Better performance
    });

    // Set text rendering to match DOM preview quality
    // This ensures the downloaded video looks identical to the preview
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Step 4: Setup MediaRecorder with audio
    const canvasStream = canvas.captureStream(frameRate);

    // Create a MediaStream that will contain both video and audio
    combinedStream = new MediaStream();

    // Add video track from canvas
    canvasStream.getVideoTracks().forEach(track => {
      combinedStream.addTrack(track);
    });

    // Extract audio using Web Audio API
    // createMediaElementSource() disconnects audio from speakers automatically
    // So the user won't hear playback, but we can capture the audio
    let audioSource = null;
    let audioDestination = null;

    try {
      // Create audio context
      audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Resume audio context if suspended (autoplay policy)
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
        console.log('🔊 Audio context resumed');
      }

      // Create source from video element - this DISCONNECTS audio from speakers
      audioSource = audioContext.createMediaElementSource(video);

      // Create destination for capture
      audioDestination = audioContext.createMediaStreamDestination();

      // Connect: video audio -> capture destination (NOT to speakers)
      audioSource.connect(audioDestination);

      // Get the audio track from the destination
      const audioTracks = audioDestination.stream.getAudioTracks();

      if (audioTracks.length > 0) {
        console.log('✅ Audio track captured via Web Audio API');
        console.log('🔇 Audio routed to capture only (not speakers)');
        audioTracks.forEach(track => {
          combinedStream.addTrack(track);
        });
      } else {
        console.warn('⚠️ No audio track in destination stream');
      }
    } catch (audioError) {
      console.warn('⚠️ Could not capture audio via Web Audio API:', audioError.message);

      // Fallback: try captureStream method (audio WILL play through speakers)
      try {
        console.log('📢 Falling back to captureStream (audio may play through speakers)');
        const videoStream = video.captureStream ? video.captureStream() : video.mozCaptureStream();
        const audioTracks = videoStream.getAudioTracks();
        if (audioTracks.length > 0) {
          console.log('✅ Audio track found via fallback method');
          audioTracks.forEach(track => combinedStream.addTrack(track));
        } else {
          console.warn('⚠️ No audio tracks in video stream');
        }
      } catch (fallbackError) {
        console.warn('⚠️ Audio capture fallback also failed:', fallbackError.message);
      }
    }

    const mimeType = getOptimalMimeType();
    const bitrate = quality === 'high' ? 8000000 : 4000000; // 8 Mbps or 4 Mbps

    // Warn if not using MP4 (won't play on iPhone/WhatsApp)
    if (!mimeType.includes('mp4') && !mimeType.includes('h264')) {
      console.warn('⚠️ WARNING: Using WebM format - may not play on iPhone or WhatsApp!');
      console.warn('💡 TIP: Use Safari browser for MP4 output, or convert the video after download');
    }

    const mediaRecorder = new MediaRecorder(combinedStream, {
      mimeType,
      videoBitsPerSecond: bitrate
    });

    const chunks = [];
    mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunks.push(e.data);
      }
    };

    // Use longer timeslice on mobile to reduce overhead (fewer, larger chunks)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const timeslice = isMobile ? 500 : 100;

    // Start recording
    mediaRecorder.start(timeslice);
    console.log('🔴 Recording started');

    // Step 5: Play video and render frames in sync
    // We use requestAnimationFrame to draw frames while the video plays naturally
    // This preserves audio continuity
    video.currentTime = 0;
    await video.play();

    let frameCount = 0;

    console.log(`🎞️  Rendering with audio preservation...`);

    // Rendering loop: draw EVERY requestAnimationFrame tick so the canvas
    // always has a fresh frame when captureStream samples it.
    // This eliminates stale/duplicate frames that cause stuttering.
    const renderFrame = () => {
      return new Promise((resolve) => {
        const draw = () => {
          if (video.currentTime >= duration || video.ended) {
            resolve();
            return;
          }

          // Draw current video frame to canvas every tick
          ctx.drawImage(video, 0, 0, videoWidth, videoHeight);

          // Get current time from video
          const currentTime = video.currentTime;

          // Filter visible text layers for current time
          const visibleLayers = textLayers.filter(layer =>
            currentTime >= (layer.startTime || 0) &&
            currentTime <= (layer.endTime || Infinity)
          );

          // Draw each visible text layer with proper font scaling
          visibleLayers.forEach(layer => {
            drawTextLayer(ctx, layer, videoWidth, videoHeight, fontScale);
          });

          frameCount++;

          // Report progress (0-50% for rendering phase if converting to MP4, else 0-100%)
          if (onProgress && frameCount % 10 === 0) {
            const renderProgress = Math.min(Math.round((currentTime / duration) * 100), 99);
            const adjustedProgress = convertToMP4Format ? Math.round(renderProgress / 2) : renderProgress;
            onProgress(adjustedProgress);
          }

          // Continue to next frame
          requestAnimationFrame(draw);
        };

        requestAnimationFrame(draw);
      });
    };

    await renderFrame();

    console.log('✅ All frames rendered with audio');

    // Step 6: Stop recording and create blob
    mediaRecorder.stop();

    const blob = await new Promise((resolve, reject) => {
      mediaRecorder.onstop = () => {
        if (chunks.length === 0) {
          reject(new Error('No video data recorded'));
          return;
        }

        const videoBlob = new Blob(chunks, { type: mimeType });
        console.log('✅ Video blob created:', {
          size: `${(videoBlob.size / 1024 / 1024).toFixed(2)} MB`,
          type: mimeType,
          canPlayType: document.createElement('video').canPlayType(mimeType)
        });
        resolve(videoBlob);
      };

      mediaRecorder.onerror = (e) => {
        reject(new Error('MediaRecorder error: ' + e.error));
      };

      // Timeout after 10 seconds
      setTimeout(() => reject(new Error('Recording finalization timeout')), 10000);
    });

    // Progress will continue in conversion phase if needed
    if (onProgress && !convertToMP4Format) {
      onProgress(100); // Only set to 100% if no conversion step follows
    }

    // Cleanup rendering resources to free memory before conversion
    video.pause();
    combinedStream.getTracks().forEach(track => track.stop());
    if (audioContext && audioContext.state !== 'closed') {
      try { await audioContext.close(); } catch (e) { /* ignore */ }
      audioContext = null;
    }
    video.src = '';
    video.load();
    if (video.parentNode) video.parentNode.removeChild(video);
    video = null;
    combinedStream = null;

    console.log('🎉 Video rendering complete!');

    // Convert to MP4 for universal compatibility (iPhone, WhatsApp, etc.)
    if (convertToMP4Format) {
      console.log('🔄 Converting to MP4 for iPhone/WhatsApp compatibility...');

      // Update progress callback for conversion phase
      const conversionProgress = (percent) => {
        if (onProgress) {
          // Conversion is 50-100% of total progress
          const totalProgress = 50 + Math.round(percent / 2);
          onProgress(totalProgress);
        }
      };

      try {
        const mp4Blob = await convertToMP4(blob, {
          quality,
          onProgress: conversionProgress
        });

        console.log('✅ MP4 conversion complete!');

        return {
          blob: mp4Blob,
          mimeType: 'video/mp4',
          isMP4Compatible: true
        };
      } catch (conversionError) {
        // FFmpeg conversion failed - fall back to original WebM
        console.warn('⚠️ MP4 conversion failed, falling back to WebM:', conversionError.message);
        console.warn('💡 Note: WebM may not play on iPhone. Use Safari browser for MP4 output.');

        if (onProgress) {
          onProgress(100);
        }

        return {
          blob,
          mimeType,
          isMP4Compatible: false,
          conversionFailed: true,
          conversionError: conversionError.message
        };
      }
    } else {
      // Return original blob without conversion
      return {
        blob,
        mimeType,
        isMP4Compatible: mimeType.includes('mp4') || mimeType.includes('h264')
      };
    }

  } catch (error) {
    console.error('❌ Video rendering error:', error);
    throw error;
  } finally {
    // Ensure all resources are freed even if rendering fails mid-way
    if (combinedStream) {
      combinedStream.getTracks().forEach(track => track.stop());
    }
    if (audioContext && audioContext.state !== 'closed') {
      try { audioContext.close(); } catch (e) { /* ignore */ }
    }
    if (video) {
      video.pause();
      video.src = '';
      video.load();
      if (video.parentNode) video.parentNode.removeChild(video);
    }
  }
};

/**
 * Check if browser supports video rendering
 * @returns {Object} Support status and details
 */
export const checkBrowserSupport = () => {
  const canvas = document.createElement('canvas');
  const canCaptureStream = typeof canvas.captureStream === 'function';
  const hasMediaRecorder = typeof MediaRecorder !== 'undefined';

  const allTypes = [
    'video/mp4;codecs=h264,aac',
    'video/mp4',
    'video/webm;codecs=h264,opus',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm'
  ];

  const supportedTypes = [];
  if (hasMediaRecorder) {
    allTypes.forEach(type => {
      if (MediaRecorder.isTypeSupported(type)) {
        supportedTypes.push(type);
      }
    });
  }

  const selectedType = getOptimalMimeType();
  const isMP4Compatible = selectedType.includes('mp4') || selectedType.includes('h264');
  const isSupported = canCaptureStream && hasMediaRecorder && supportedTypes.length > 0;

  return {
    isSupported,
    canCaptureStream,
    hasMediaRecorder,
    supportedTypes,
    selectedType,
    isMP4Compatible,
    iPhoneCompatible: isMP4Compatible,
    whatsappCompatible: isMP4Compatible,
    message: isSupported
      ? `Browser supports video rendering (${isMP4Compatible ? 'MP4 ✅' : 'WebM ⚠️'})`
      : 'Browser does not support video rendering',
    recommendation: isMP4Compatible
      ? 'Your videos will play on iPhone and WhatsApp ✅'
      : 'Use Safari browser for iPhone/WhatsApp compatible videos (MP4 format)'
  };
};
